/**
 * /ambassador — smart route entry that "just works" for both guests
 * and signed-in users.
 *
 * Why this wrapper exists
 * ───────────────────────
 * Founder shares `trubeaute.com/ambassador` as a single canonical
 * link. Before this wrapper, that route was wrapped in
 * <ProtectedRoute>, so guests got bounced to /login before they
 * could even see what the program was about — which made the link
 * look broken when shared on TikTok / IG / Linktree.
 *
 * The fix:
 *   • Guests → <AmbassadorsPublic /> (the public marketing + apply
 *     page).
 *   • Auth still resolving → spinner.
 *   • Signed-in users → <AmbassadorDashboard />. The dashboard
 *     itself handles the "you're authed but not yet an ambassador"
 *     empty state via its 404 branch, so we don't need to
 *     pre-check ambassador-role here.
 *
 * /ambassadors (plural) keeps pointing at AmbassadorsPublic for
 * back-compat with older share links. Going forward, share the
 * singular URL — it's shorter and matches the singular pronouns
 * the founder uses everywhere else.
 */
import React from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AmbassadorsPublic from "./AmbassadorsPublic";
import AmbassadorDashboard from "./AmbassadorDashboard";

const AmbassadorEntry = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"
        data-testid="ambassador-entry-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-[#9B6A6C]" />
      </div>
    );
  }

  if (!user) {
    return <AmbassadorsPublic />;
  }

  return <AmbassadorDashboard />;
};

export default AmbassadorEntry;
